import { useState } from "react";
import { getFontFamily } from "../constants/colors";
import { getDashboardStats, getStreakDays } from "../progress";
import { getCurrentTier, getUsageStats, activateCode, TIERS } from "../subscription";
import { FeedbackForm } from "./FeedbackWidget";
import soundManager from "../sounds/sound-manager";

// PROFILE-FIX-7-APPLIED: grouped meta row component
function MetaRow({ icon, label, onClick, subtle, ff, borderTop }) {
  return (
    <div
      className={onClick ? "tactile-card" : undefined}
      onClick={onClick || undefined}
      style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "14px 20px", minHeight: 44,
        cursor: onClick ? "pointer" : "default",
        borderTop: borderTop ? "1px solid rgba(140,110,60,0.06)" : "none",
      }}
    >
      <span style={{ fontSize: 16, width: 24, textAlign: "center" }}>{icon}</span>
      <span style={{
        fontSize: 14,
        fontWeight: subtle ? 400 : 500,
        // PROFILE-FIX-3-APPLIED: warm tokens on white
        color: subtle ? "#8B7355" : "#615030",
        fontFamily: ff,
      }}>
        {label}
      </span>
    </div>
  );
}

export function SettingsPage({ profile, lang, setLang, onBack, onLogout, onClassChange, onBoardChange, onUpgrade }) {
  const ff = getFontFamily(lang);
  const isHi = lang === "hi";
  const initial = (profile?.name || "S").charAt(0).toUpperCase();
  const photoURL = profile?.photoURL;
  const action = "#1A7A52";

  const stats = getDashboardStats(profile?.studentClass);
  const totalConcepts = stats.conceptsExplored || 0;
  const daysActive = stats.daysActive || 0;
  const streakDays = getStreakDays(stats?.activeDaysList) || 0;
  const recentChapters = stats.recentChapters || [];

  // Subscription state
  const [tier, setTierState] = useState(getCurrentTier());
  const tierInfo = TIERS[tier];
  const usage = getUsageStats();
  const [codeInput, setCodeInput] = useState("");
  const [codeMsg, setCodeMsg] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  // CLASS-10-ONLY-BETA-APPLIED: ephemeral message shown when user taps a locked class.
  const [classLockedMsg, setClassLockedMsg] = useState(null);
  // SHARE-FEEDBACK-APPLIED: ephemeral toast shown after a share/copy attempt so the
  // user always sees what happened (Web Share API can silently no-op on some browsers).
  const [shareMsg, setShareMsg] = useState(null);
  // SOUND-APPLIED: mirror the singleton's mute state for the toggle UI.
  // Default is sound ON (muted=false) per spec.
  const [soundMuted, setSoundMuted] = useState(soundManager.isMuted());
  const toggleSound = () => {
    const next = !soundMuted;
    soundManager.setMuted(next);
    setSoundMuted(next);
    // Audible confirmation when turning sound ON — feels responsive
    if (!next) soundManager.play("tap");
  };

  const handleCodeSubmit = () => {
    const result = activateCode(codeInput);
    if (result.success) {
      setTierState(result.tier);
      setCodeMsg({ type: "success", text: isHi ? `${result.tierInfo.name} activated!` : `${result.tierInfo.name} plan activated!` });
      setCodeInput("");
    } else {
      setCodeMsg({ type: "error", text: isHi ? "गलत कोड" : "Invalid code" });
    }
  };

  const handleShare = async () => {
    // SHARE-COPY-UPDATED: positions Samajh as a study companion app (not "AI tutor / Socratic"),
    // and points to the production domain.
    const url = "https://samajhapp.com/";
    const tagline = isHi
      ? "Samajh try करो — तुम्हारा study companion app!"
      : "Try Samajh — your study companion app.";
    const fullText = `${tagline} ${url}`;
    const showToast = (kind, copy) => {
      setShareMsg({ kind, copy });
      clearTimeout(window.__samajhShareToastTimer);
      window.__samajhShareToastTimer = setTimeout(() => setShareMsg(null), 2800);
    };

    // 1. Try Web Share API (mobile + supported browsers)
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      try {
        await navigator.share({ title: "Samajh", text: tagline, url });
        return; // success — OS share sheet shown; no toast needed
      } catch (err) {
        // User cancellation = silent no-op (don't pester with toast)
        if (err && (err.name === "AbortError" || err.name === "NotAllowedError")) return;
        console.warn("[share] navigator.share failed:", err);
        // Fall through to clipboard
      }
    }

    // 2. Fallback: copy to clipboard
    if (typeof navigator !== "undefined" && navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
      try {
        await navigator.clipboard.writeText(fullText);
        showToast("ok", isHi ? "लिंक कॉपी हो गया!" : "Link copied to clipboard!");
        return;
      } catch (err) {
        console.warn("[share] clipboard.writeText failed:", err);
      }
    }

    // 3. Last resort: prompt with the text so the user can copy manually
    try {
      window.prompt(isHi ? "इस लिंक को कॉपी करें:" : "Copy this link:", fullText);
    } catch {
      showToast("err", isHi ? "शेयर नहीं हो पाया" : "Couldn't share — try again");
    }
  };
  const handlePrivacy = () => {
    window.open("https://samajhapp.com/privacy", "_blank", "noopener,noreferrer");
  };

  // EMAIL-DELETION-APPLIED: opens a pre-filled deletion request to support; we process it
  // manually (Firebase Console / Admin SDK) and confirm. Clients have no delete rights.
  const handleDeleteAccount = () => {
    const subject = encodeURIComponent("Delete my Samajh account");
    const body = encodeURIComponent(
      "Hi Samajh team,\n\nPlease delete my account and all my learning data.\n\n(Sent from my registered email so you can verify the account.)\n\nThank you."
    );
    window.location.href = `mailto:samajh.tutor5@gmail.com?subject=${subject}&body=${body}`;
  };

  // PROFILE-FIX-2-APPLIED: plan label — "Free Plan" instead of "FREE · LIFETIME"
  const planLabel = tier === "free"
    ? (isHi ? "मुफ़्त प्लान" : "Free Plan")
    : `${tierInfo.name} · ${tierInfo.label}`;

  // PROFILE-FIX-6-APPLIED: streak styling
  const isStreakActive = streakDays > 0;

  return (<>
    <style>{`
      .profile-code-input::placeholder { color: #8B7355; opacity: 1; }
    `}</style>
    <div style={{ height: "100vh", background: "linear-gradient(180deg, #7EB3D4 0%, #96C4DE 10%, #ADD4E8 22%, #C4E1F0 34%, #D8ECF5 46%, #E8F2F8 58%, #F1F7FA 70%, #F7FAFB 82%, #F9FBFC 100%)", fontFamily: ff, display: "flex", flexDirection: "column", overflow: "hidden" }}>

      {/* PROFILE-FIX-1-APPLIED: header renamed "Settings" → "Profile" */}
      <div className="animate-in stagger-0" style={{ padding: "16px 20px", display: "flex", alignItems: "center", gap: 12 }}>
        <button className="tactile-pill" onClick={onBack} style={{ background: "none", border: "none", cursor: "pointer", padding: 4, display: "flex", minWidth: 44, minHeight: 44, alignItems: "center", justifyContent: "center" }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M15 18l-6-6 6-6" stroke={action} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </button>
        <span style={{ fontSize: 16, fontWeight: 600, color: "#0F172A" }}>{isHi ? "प्रोफ़ाइल" : "Profile"}</span>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "0 20px 20px" }}>

        {/* Profile card — PROFILE-FIX-3-APPLIED email + class subtitle contrast */}
        <div className="animate-in stagger-0" style={{ background: "white", borderRadius: 18, padding: 20, marginBottom: 12, display: "flex", alignItems: "center", gap: 14, boxShadow: "0 2px 8px rgba(140,110,60,0.08)" }}>
          {photoURL ? (
            <img src={photoURL} alt="" style={{ width: 52, height: 52, borderRadius: "50%", objectFit: "cover" }} />
          ) : (
            <div style={{ width: 52, height: 52, borderRadius: "50%", background: "#E8F5EE", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ fontSize: 20, fontWeight: 600, color: action }}>{initial}</span>
            </div>
          )}
          <div>
            <div style={{ fontSize: 16, fontWeight: 600, color: "#0F172A" }}>{profile?.name || "Student"}</div>
            <div style={{ fontSize: 12, color: "#8B7355", marginTop: 2 }}>{profile?.email || ""}</div>
            <div style={{ fontSize: 11, color: "#8B7355", marginTop: 2 }}>
              {isHi ? `कक्षा ${profile?.studentClass} · ${profile?.board || "CBSE"}` : `Class ${profile?.studentClass} · ${profile?.board || "CBSE"}`}
            </div>
          </div>
        </div>

        {/* ── SUBSCRIPTION ── PROFILE-FIX-2, 3, 5 APPLIED */}
        <div className="animate-scale-in stagger-1" style={{ background: "white", borderRadius: 18, padding: 20, marginBottom: 12, boxShadow: "0 2px 8px rgba(140,110,60,0.08)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#0F172A", letterSpacing: "-0.02em" }}>{isHi ? "आपका प्लान" : "Your plan"}</div>
            <span style={{
              fontSize: 12, fontWeight: 600,
              color: tier === "pro" ? "#1A7A52" : tier === "lite" ? "#2A6A8A" : "#1A7A52",
              background: tier === "pro" ? "rgba(26,122,82,0.1)" : tier === "lite" ? "rgba(42,106,138,0.1)" : "rgba(26,122,82,0.1)",
              borderRadius: 8, padding: "4px 10px", fontFamily: ff,
            }}>
              {planLabel}
            </span>
          </div>

          {/* Usage bars (only for lite/pro) */}
          {tier !== "free" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#615030", marginBottom: 4 }}>
                  <span>{isHi ? "AI sessions" : "AI sessions"}</span>
                  <span style={{ fontWeight: 700 }}>{usage.aiChat.used}/{tier === "pro" ? "∞" : usage.aiChat.limit}</span>
                </div>
                <div style={{ height: 6, background: "#E8EFF5", borderRadius: 3 }}>
                  <div style={{ height: "100%", borderRadius: 3, background: tier === "pro" ? action : "#2A6A8A", width: tier === "pro" ? `${Math.min(usage.aiChat.used / 80 * 100, 100)}%` : `${Math.min(usage.aiChat.used / usage.aiChat.limit * 100, 100)}%`, transition: "width 0.3s" }} />
                </div>
              </div>
            </div>
          )}

          {/* Upgrade button (free/lite) — PROFILE-FIX-5-APPLIED tactile-btn-primary */}
          {tier !== "pro" && (
            <div className="tactile-btn-primary" onClick={onUpgrade} style={{
              width: "100%", padding: "12px", textAlign: "center",
              fontSize: 13, fontWeight: 700, cursor: "pointer",
              background: tier === "free" ? action : "linear-gradient(135deg, #1A7A52, #2AAA72)",
              color: "white",
              borderBottom: "3px solid #15633F",
            }}>
              {isHi ? "Pro में अपग्रेड करें" : "Upgrade to Pro"}
            </div>
          )}

          {/* Code entry — PROFILE-FIX-3C, 3D, 5 APPLIED */}
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 11, color: "#8B7355", marginBottom: 6, fontWeight: 500 }}>{isHi ? "कोड है?" : "Have a code?"}</div>
            <div style={{ display: "flex", gap: 6 }}>
              <input
                type="text"
                className="profile-code-input"
                value={codeInput}
                onChange={(e) => { setCodeInput(e.target.value.toUpperCase()); setCodeMsg(null); }}
                placeholder={isHi ? "कोड डालें" : "Enter code"}
                style={{
                  flex: 1, padding: "8px 12px", borderRadius: 8,
                  border: "1px solid rgba(140,110,60,0.15)", fontSize: 13, fontWeight: 600,
                  fontFamily: ff, color: "#0F172A", background: "#FFFFFF",
                  outline: "none", letterSpacing: "0.05em", minHeight: 44,
                }}
              />
              <div
                className="tactile-btn-primary"
                onClick={codeInput.trim() ? handleCodeSubmit : undefined}
                style={{
                  padding: "8px 16px", display: "flex", alignItems: "center", justifyContent: "center",
                  background: codeInput.trim() ? action : "rgba(26,122,82,0.15)",
                  color: codeInput.trim() ? "white" : "#8B7355",
                  fontSize: 13, fontWeight: 700,
                  cursor: codeInput.trim() ? "pointer" : "default",
                  fontFamily: ff, minHeight: 44,
                  borderBottom: codeInput.trim() ? "3px solid #15633F" : "3px solid transparent",
                  pointerEvents: codeInput.trim() ? "auto" : "none",
                }}
              >
                {isHi ? "लागू" : "Apply"}
              </div>
            </div>
            {codeMsg && (
              <div style={{ marginTop: 6, fontSize: 11, fontWeight: 600, color: codeMsg.type === "success" ? "#1A7A52" : "#CC4444" }}>
                {codeMsg.type === "success" ? "✓ " : "✗ "}{codeMsg.text}
              </div>
            )}
          </div>
        </div>

        {/* ── YOUR JOURNEY ── PROFILE-FIX-3E, 5, 6 APPLIED */}
        <div className="animate-in stagger-2" style={{ background: "white", borderRadius: 18, padding: 20, marginBottom: 12, boxShadow: "0 2px 8px rgba(140,110,60,0.08)" }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#0F172A", marginBottom: 14, letterSpacing: "-0.02em" }}>{isHi ? "आपकी यात्रा" : "Your journey"}</div>

          {/* Stats row — FIX-6: streak celebration when > 0 */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 16 }}>
            {/* Concepts */}
            <div className="tactile-card" style={{ background: "#E8F5EE", borderRadius: 12, padding: "14px 8px", textAlign: "center", minHeight: 80, display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: action, letterSpacing: "-0.02em" }}>{totalConcepts}</div>
              <div style={{ fontSize: 11, color: "#615030", marginTop: 3, fontWeight: 500 }}>{isHi ? "अवधारणाएं" : "concepts"}</div>
            </div>

            {/* Streak — celebratory when > 0 */}
            <div className="tactile-card" style={{
              background: isStreakActive ? "rgba(232,168,23,0.10)" : "rgba(140,110,60,0.06)",
              borderRadius: 12, padding: "14px 8px", textAlign: "center",
              minHeight: 80, display: "flex", flexDirection: "column", justifyContent: "center",
            }}>
              <div style={{
                fontSize: 22, fontWeight: 700, letterSpacing: "-0.02em",
                color: isStreakActive ? "#E8A817" : action,
              }}>
                {isStreakActive ? `🔥${streakDays}` : "0"}
              </div>
              <div style={{ fontSize: 11, color: "#615030", marginTop: 3, fontWeight: 500 }}>{isHi ? "दिन स्ट्रीक" : "day streak"}</div>
            </div>

            {/* Days active */}
            <div className="tactile-card" style={{ background: "#E8F5EE", borderRadius: 12, padding: "14px 8px", textAlign: "center", minHeight: 80, display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: action, letterSpacing: "-0.02em" }}>{daysActive}</div>
              <div style={{ fontSize: 11, color: "#615030", marginTop: 3, fontWeight: 500 }}>{isHi ? "सक्रिय दिन" : "days active"}</div>
            </div>
          </div>

          {/* Recent chapters — PROFILE-FIX-3F, 5 APPLIED */}
          {recentChapters.length > 0 && (<>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#615030", marginBottom: 8, letterSpacing: "0.02em" }}>{isHi ? "हाल के अध्याय" : "Recent chapters"}</div>
            {recentChapters.slice(0, 3).map((ch, i) => (
              <div key={i} className="tactile-card" style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderTop: i > 0 ? "1px solid rgba(140,110,60,0.08)" : "none", minHeight: 44 }}>
                <div style={{ width: 6, height: 6, borderRadius: "50%", background: action, flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: "#0F172A" }}>{ch.chapter}</div>
                  <div style={{ fontSize: 11, color: "#8B7355" }}>{ch.subject}</div>
                </div>
              </div>
            ))}
          </>)}

          {/* Empty state */}
          {recentChapters.length === 0 && (
            <div style={{ textAlign: "center", padding: "12px 0", color: "#8B7355", fontSize: 12 }}>
              {isHi ? "अभी तक कोई अध्याय नहीं देखा" : "No chapters explored yet. Start learning!"}
            </div>
          )}
        </div>

        {/* Class — PROFILE-FIX-4, 5 APPLIED: 44px touch + tactile-pill */}
        <div className="animate-in stagger-4" style={{ background: "white", borderRadius: 16, padding: 16, marginBottom: 10, boxShadow: "0 2px 8px rgba(140,110,60,0.08)" }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#0F172A", marginBottom: 10 }}>{isHi ? "कक्षा" : "Class"}</div>
          {/* CLASS-10-ONLY-BETA-APPLIED: only Class 10 active; others show lock icon + on-tap toast. */}
          <div style={{ display: "flex", gap: 8 }}>
            {[6, 7, 8, 9, 10].map(cls => {
              const selected = cls === profile?.studentClass;
              const isAvailable = cls === 10;
              return (
                <div
                  key={cls}
                  className={isAvailable ? "tactile-pill" : ""}
                  onClick={() => {
                    if (isAvailable) {
                      if (!selected) onClassChange?.(cls);
                      return;
                    }
                    // Locked tap → show ephemeral message so user knows it's intentional
                    setClassLockedMsg(cls);
                    clearTimeout(window.__samajhClassLockedTimer);
                    window.__samajhClassLockedTimer = setTimeout(() => setClassLockedMsg(null), 2500);
                  }}
                  style={{
                    flex: 1, textAlign: "center", minHeight: 56, padding: "6px 2px",
                    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2,
                    borderRadius: 12, cursor: isAvailable ? "pointer" : "not-allowed",
                    background: selected ? action : (isAvailable ? "transparent" : "rgba(140,110,60,0.06)"),
                    color: selected ? "white" : (isAvailable ? "#615030" : "#9A8870"),
                    border: selected ? "none" : `1.5px solid rgba(140,110,60,${isAvailable ? 0.2 : 0.12})`,
                    opacity: isAvailable ? 1 : 0.55,
                    transition: "all .15s",
                  }}
                >
                  <div style={{ fontSize: 15, fontWeight: 600, lineHeight: 1, display: "flex", alignItems: "center", gap: 3 }}>
                    {!isAvailable && (
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.85 }}>
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                      </svg>
                    )}
                    {cls}
                  </div>
                  {!isAvailable && (
                    <div style={{ fontSize: 8, fontWeight: 600, color: "#9A8870", letterSpacing: "0.04em", lineHeight: 1, textTransform: "uppercase" }}>
                      {isHi ? "जल्द" : "Soon"}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {/* Inline toast for locked-class taps */}
          {classLockedMsg && (
            <div style={{
              marginTop: 10, padding: "10px 12px", borderRadius: 10,
              background: "#FEF3C7", border: "1px solid #FDE68A",
              color: "#854D0E", fontSize: 12, fontWeight: 500,
              textAlign: "center", lineHeight: 1.4,
            }}>
              {isHi
                ? `कक्षा ${classLockedMsg} जल्द आ रही है — अभी सिर्फ कक्षा 10 उपलब्ध है`
                : `Class ${classLockedMsg} is coming soon — only Class 10 is available during beta`}
            </div>
          )}
        </div>

        {/* Board — PROFILE-FIX-4, 5 APPLIED */}
        <div className="animate-in stagger-4" style={{ background: "white", borderRadius: 16, padding: 16, marginBottom: 10, boxShadow: "0 2px 8px rgba(140,110,60,0.08)" }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#0F172A", marginBottom: 10 }}>{isHi ? "बोर्ड" : "Board"}</div>
          <div style={{ display: "flex", gap: 10 }}>
            {["CBSE", "ICSE"].map(b => {
              const selected = b === (profile?.board || "CBSE");
              return (
                <div key={b} className="tactile-pill" onClick={() => { if (!selected) onBoardChange?.(b); }} style={{
                  flex: 1, textAlign: "center", minHeight: 44,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  borderRadius: 12, fontSize: 14, fontWeight: 600, cursor: "pointer",
                  background: selected ? action : "transparent",
                  color: selected ? "white" : "#615030",
                  border: selected ? "none" : "1.5px solid rgba(140,110,60,0.2)",
                  transition: "all .15s",
                }}>{b}</div>
              );
            })}
          </div>
        </div>

        {/* Language — PROFILE-FIX-4, 5 APPLIED */}
        <div className="animate-in stagger-4" style={{ background: "white", borderRadius: 16, padding: 16, marginBottom: 10, boxShadow: "0 2px 8px rgba(140,110,60,0.08)" }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#0F172A", marginBottom: 10 }}>{isHi ? "भाषा" : "Language"}</div>
          <div style={{ display: "flex", gap: 10 }}>
            {[{ code: "en", label: "English" }, { code: "hi", label: "हिंदी" }].map(l => {
              const selected = lang === l.code;
              return (
                <div key={l.code} className="tactile-pill" onClick={() => setLang(l.code)} style={{
                  flex: 1, textAlign: "center", minHeight: 44,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  borderRadius: 12, fontSize: 14, fontWeight: 600, cursor: "pointer",
                  background: selected ? action : "transparent",
                  color: selected ? "white" : "#615030",
                  border: selected ? "none" : "1.5px solid rgba(140,110,60,0.2)",
                  transition: "all .15s",
                }}>{l.label}</div>
              );
            })}
          </div>
        </div>

        {/* PROFILE-FIX-7-APPLIED: grouped meta links */}

        {/* Group 1: Social / Support */}
        <div className="animate-in stagger-5" style={{ background: "white", borderRadius: 16, overflow: "hidden", boxShadow: "0 2px 8px rgba(140,110,60,0.08)", marginBottom: 12 }}>
          <MetaRow icon="↗" label={isHi ? "दोस्तों को भेजें" : "Share with friends"} onClick={handleShare} ff={ff} />
          <MetaRow icon="💬" label={isHi ? "Feedback दें" : "Give Feedback"} onClick={() => setShowFeedback(true)} ff={ff} borderTop />
        </div>
        {/* SHARE-FEEDBACK-APPLIED: ephemeral toast right under the row, auto-clears after 2.8s. */}
        {shareMsg && (
          <div style={{
            marginTop: -6, marginBottom: 12,
            padding: "10px 12px", borderRadius: 10,
            background: shareMsg.kind === "ok" ? "#E8F5EE" : "#FEE8E8",
            border: shareMsg.kind === "ok" ? "1px solid #B8E0CC" : "1px solid #F4C2C2",
            color: shareMsg.kind === "ok" ? "#1A5A3D" : "#8B2E2E",
            fontSize: 12, fontWeight: 600, textAlign: "center",
          }}>
            {shareMsg.copy}
          </div>
        )}

        {/* Group 2: Info / Legal */}
        <div className="animate-in stagger-5" style={{ background: "white", borderRadius: 16, overflow: "hidden", boxShadow: "0 2px 8px rgba(140,110,60,0.08)", marginBottom: 12 }}>
          <MetaRow icon="🔒" label={isHi ? "गोपनीयता नीति" : "Privacy Policy"} onClick={handlePrivacy} ff={ff} />
          {/* PROFILE-FIX-3G-APPLIED: v1.0.0 readable */}
          <MetaRow icon="ℹ️" label="v1.0.0" onClick={null} subtle={true} ff={ff} borderTop />
        </div>

        {/* SOUND-APPLIED: Sounds toggle — app-wide mute for all 6 UI sound triggers */}
        <div className="animate-in stagger-5" style={{ background: "white", borderRadius: 16, overflow: "hidden", boxShadow: "0 2px 8px rgba(140,110,60,0.08)", marginBottom: 12 }}>
          <div
            onClick={toggleSound}
            role="switch"
            aria-checked={!soundMuted}
            aria-label={isHi ? "Awaaz" : "Sounds"}
            style={{
              display: "flex", alignItems: "center", gap: 12,
              padding: "14px 20px", minHeight: 44, cursor: "pointer",
              fontFamily: ff,
            }}
          >
            <span style={{ fontSize: 16, width: 24, textAlign: "center" }}>🔊</span>
            <span style={{ flex: 1, fontSize: 14, fontWeight: 500, color: "#615030" }}>
              {isHi ? "Awaaz" : "Sounds"}
            </span>
            {/* Inline pill-style switch — green when ON, grey when OFF */}
            <div style={{
              width: 44, height: 26, borderRadius: 999,
              background: soundMuted ? "rgba(140,110,60,0.18)" : "#1A7A52",
              position: "relative", transition: "background 0.18s ease",
              flexShrink: 0,
            }}>
              <div style={{
                position: "absolute", top: 2, left: soundMuted ? 2 : 20,
                width: 22, height: 22, borderRadius: "50%",
                background: "#FFFFFF",
                boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
                transition: "left 0.18s ease",
              }} />
            </div>
          </div>
        </div>

        {/* Sign out — standalone, red */}
        <div className="animate-in stagger-5 tactile-pill" onClick={onLogout} style={{
          background: "white", borderRadius: 16, padding: "14px 16px", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          boxShadow: "0 2px 8px rgba(140,110,60,0.08)",
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" stroke="#DC2626" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
          <span style={{ fontSize: 14, fontWeight: 600, color: "#DC2626", fontFamily: ff }}>{isHi ? "साइन आउट" : "Sign out"}</span>
        </div>

        {/* EMAIL-DELETION-APPLIED: opens a pre-filled deletion-request email. Subtler than
            Sign out; subtext makes the email-based process clear. */}
        <div className="animate-in stagger-5 tactile-link"
          onClick={handleDeleteAccount}
          style={{ marginTop: 14, textAlign: "center", cursor: "pointer", padding: "8px" }}>
          <span style={{ fontSize: 13, fontWeight: 500, color: "#9A8870", fontFamily: ff, textDecoration: "underline" }}>
            {isHi ? "खाता हटाएं" : "Delete account"}
          </span>
          <div style={{ fontSize: 11, color: "#B0A088", fontFamily: ff, marginTop: 3 }}>
            {isHi ? "ईमेल से अनुरोध करें, हम आपका डेटा हटा देंगे" : "Request by email, we'll delete your data"}
          </div>
        </div>

      </div>
    </div>

    {/* Feedback Form overlay */}
    {showFeedback && <FeedbackForm lang={lang} onClose={() => setShowFeedback(false)} />}

  </>
  );
}
